///
/// EarthBrowser
///
/// Created by Matt Giger on 4/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { ComponentType } from "react"
import type { LucideIcon } from "lucide-react"
import {
	Globe,
	Move3D,
	Layers,
	LayoutGrid,
	Bookmark,
	MapPin,
	BookOpenText,
	Clock,
	Rocket,
	FileUp,
} from "lucide-react"
import { CameraControlsDiagram } from "./CameraControlsDiagram"
import { cStoryAuthoringEnabled } from "@/utils/featureGates"

export interface OnboardingStep {
	id: string
	title: string
	description: string
	icon: LucideIcon
	color: string
	tips: string[]
	badge?: string
	content?: ComponentType
}

const allSteps: OnboardingStep[] = [
	{
		id: "welcome",
		title: "Welcome to EarthBrowser!",
		description: "Explore our planet with stunning real-time visualizations of earthquakes, satellites, weather, and more. Here's a quick tour to get you started.",
		icon: Globe,
		color: "from-blue-500 to-cyan-500",
		tips: [
			"This tour takes about a minute",
			"You can replay it anytime from the account menu in the top-right",
			"Use the buttons below to navigate through the steps",
		],
		badge: "Beta",
	},
	{
		id: "globe-navigation",
		title: "Navigate the Globe",
		description: "The 3D globe is your main workspace. Move around it naturally using your mouse or trackpad.",
		icon: Globe,
		color: "from-cyan-500 to-teal-500",
		tips: [
			"Click and drag to pan the globe",
			"Scroll or pinch to zoom in and out, about the point under your cursor",
			"One-handed zoom: tap, then press and drag down to zoom in or up to zoom out",
			"The camera stack in the bottom-right has 2D/3D, orbit, fly-to-my-location and zoom",
			"Click the compass to snap back to north-up",
		],
	},
	{
		id: "camera-controls",
		title: "Camera Tilt & Orientation",
		description: "Go beyond basic panning to control the camera's tilt and bearing for dramatic perspectives.",
		icon: Move3D,
		color: "from-indigo-500 to-blue-500",
		content: CameraControlsDiagram,
		tips: [],
	},
	{
		id: "layers-panel",
		title: "Layers & Search",
		description: "The Layers panel is what's on the globe; Search finds anything, anywhere.",
		icon: Layers,
		color: "from-violet-500 to-purple-500",
		tips: [
			"Tap the Layers button in the top-left; layers are grouped by domain: Atmosphere, Ocean, Land & solid earth, Space, and Reference",
			"Flip a switch to show or hide that layer on the globe; coloured icons are the layers that are on. Tap the count in the header to see just those",
			"Press \u2318K (Mac) or Ctrl+K (Windows), or tap Search, to find cities, satellites, earthquakes, volcanoes, layers and actions",
			"Select a search result to fly to it",
		],
	},
	{
		id: "data-browsers",
		title: "The Data Dock",
		description: "Every dataset on the globe has a searchable, sortable table. On desktop they live in the data dock along the right edge; on phones they slide up from the bottom.",
		icon: LayoutGrid,
		color: "from-orange-500 to-red-500",
		tips: [
			"Switch a layer on and its icon appears on the dock's rail; click the icon to open the table",
			"The table chip on a Layers row, or the table button on any info card, opens that dataset's table too",
			"Click a row to fly to it; the globe centers in the space beside the dock",
			"Drag the dock's edge to resize it, drag a rail icon up or down to reorder, or drag it left to float the table as a window",
			"Press \u2318` (Mac) or Ctrl+` (Windows) to collapse the dock to just the rail",
		],
	},
	{
		id: "import-data",
		title: "Bring Your Own Data",
		description: "Drop your own files straight onto the globe. Tabular data becomes browsable points, and images drape over the terrain as map overlays.",
		icon: FileUp,
		color: "from-cyan-500 to-blue-500",
		tips: [
			"Drag & drop GeoJSON, CSV, shapefiles (.zip), KML/KMZ, or GeoPackage files anywhere on the page",
			"Drop a PNG, JPEG, or GeoTIFF and it drapes on the globe \u2014 GeoTIFFs place themselves from their georeference",
			cStoryAuthoringEnabled
				? "Sign in and save imports to the cloud to share them or use them in story tours"
				: "Sign in and save imports to the cloud to share them",
		],
	},
	{
		id: "saved-views",
		title: "Saved Views",
		description: "Bookmark camera positions you want to return to \u2014 your favorite places, storm systems, or cinematic angles.",
		icon: Bookmark,
		color: "from-emerald-500 to-green-500",
		tips: [
			"Tap the bookmark in the top-right, or press \u2318D (Mac) / Ctrl+D (Windows), to save the current view",
			"The share button beside it copies a link to exactly what you're looking at",
			"The chevron next to the bookmark opens your Views menu: saved views, places and recent items",
			"Select a saved view to fly right back to it",
		],
	},
	{
		id: "placemarks",
		title: "Placemarks",
		description: "Pin your own places on the globe. Each placemark shows live conditions and weather alerts for its location \u2014 and one placemark can hold many locations, like every stop on a trip or all your field sites.",
		icon: MapPin,
		color: "from-rose-500 to-red-500",
		tips: [
			"Press and hold (or right-click) anywhere on the globe to drop a pin",
			"The pin menu can also add the spot to one of your existing placemarks",
			"Drag & drop a CSV or GeoJSON of points to turn the whole file into one placemark",
			"Tap a pin to see current weather and any active alerts at that spot",
			"Zoom into a multi-location placemark and it spreads into individual pins",
			"Sign in and your placemarks sync to all your devices",
		],
	},
	{
		id: "story-tours",
		title: "Story Tours",
		description: "Turn your explorations into guided tours: a story flies viewers from stop to stop while narrative cards explain what they're seeing.",
		icon: BookOpenText,
		color: "from-teal-500 to-cyan-500",
		tips: [
			"Open the Stories tab of the Views menu (chevron beside the bookmark, top-right) and create a new story",
			"Frame a shot on the globe, then Add stop \u2014 the camera and layers are captured for you",
			"Write a card for each stop and draw arrows, markers, or regions right on the globe",
			"Play advances at your pace; guided mode advances automatically",
			"Sign in to copy a share link \u2014 anyone can watch, no account needed",
		],
	},
	{
		id: "time-controls",
		title: "Time Controls",
		description: "EarthBrowser shows real-time data by default, but you can also travel through time to explore past events.",
		icon: Clock,
		color: "from-amber-500 to-orange-500",
		tips: [
			"The control bar at the bottom shows the current time; LIVE follows the real clock, and when you leave it the chip reads how far from now you are",
			"Drag the playhead or press play to move through the time window; the colored lanes under it show which layers have data when",
			"Pick a speed from 1x up to a full day per second from the label beside the play button",
			"Expand the bar to set how much history and forecast the window covers",
		],
	},
	{
		id: "ready",
		title: "You're All Set!",
		description: "You've got the basics. Start exploring and don't hesitate to share feedback \u2014 your input shapes what we build next.",
		icon: Rocket,
		color: "from-blue-600 to-cyan-600",
		tips: [
			"Remember: \u2318K opens Search for quick access to everything",
			"This tutorial is always available from the account menu in the top-right",
		],
	},
]

// Story authoring is hidden for the initial release, so its step is too
export const onboardingSteps: OnboardingStep[] = allSteps.filter(step =>
	cStoryAuthoringEnabled || step.id !== "story-tours"
)

export const TOTAL_STEPS = onboardingSteps.length
